import { ref, set, update, onValue, onDisconnect, remove, serverTimestamp } from 'firebase/database';
import { rtdb } from './firebase';
import { RTDB_PATHS } from '../utils/constants';
import { createUserPresenceData } from '../utils/helpers';
import { withErrorHandling, ERROR_TYPES, createError, logError } from '../utils/errorHandling';

/**
 * Update cursor position for a user in real-time database
 * @param {string} userId - User ID
 * @param {number} x - Cursor X position
 * @param {number} y - Cursor Y position
 * @param {string} displayName - User's display name
 * @param {string} color - User's cursor color
 * @returns {Promise<Object>} Result with success status
 */
export async function updateCursorPosition(userId, x, y, displayName, color) {
  return await withErrorHandling(
    async () => {
      // Validate inputs
      if (!userId || typeof x !== 'number' || typeof y !== 'number') {
        throw createError(
          'User ID and valid cursor coordinates are required',
          ERROR_TYPES.VALIDATION,
          'medium'
        );
      }

      const userSessionPath = RTDB_PATHS.getUserSession(userId);
      const userRef = ref(rtdb, userSessionPath);
      
      // Use update() instead of set() to preserve other session data (isOnline, etc.)
      await update(userRef, {
        cursorX: x,
        cursorY: y,
        lastSeen: serverTimestamp()
      });

      return {
        success: true,
        message: 'Cursor position updated'
      };
    },
    'Update Cursor Position',
    { 
      errorType: ERROR_TYPES.NETWORK,
      enableRetry: false, // Don't retry - cursor updates happen frequently
      enableOfflineQueue: false, // Don't queue - cursor position is transient data
      logErrors: false // Silent errors - cursor updates are non-critical
    }
  );
}

/**
 * Subscribe to all cursor positions and user presence
 * @param {Function} callback - Function to call with cursors data
 * @param {string} currentUserId - Current user's ID to filter out from results
 * @returns {Function} unsubscribe function
 */
export function subscribeToCursors(callback, currentUserId) {
  try {
    const sessionsRef = ref(rtdb, RTDB_PATHS.USER_SESSIONS);
    
    const unsubscribe = onValue(sessionsRef, 
      (snapshot) => {
        try {
          const cursors = {};
          
          if (snapshot.exists()) {
            const allSessions = snapshot.val();
            
            // Filter out current user and format cursor data
            Object.entries(allSessions).forEach(([userId, userData]) => {
              // Skip current user's cursor
              if (userId !== currentUserId && userData) {
                cursors[userId] = {
                  userId,
                  displayName: userData.displayName || 'Anonymous',
                  cursorColor: userData.cursorColor || '#666666',
                  cursorX: userData.cursorX || 0,
                  cursorY: userData.cursorY || 0,
                  lastSeen: userData.lastSeen || Date.now(),
                  isOnline: true // If they're in the sessions, they're considered online
                };
              }
            });
          }
          
          callback(cursors);
          console.log('🖱️ Cursors updated:', Object.keys(cursors).length, 'active users');
        } catch (error) {
          logError(createError(error, ERROR_TYPES.NETWORK, 'medium'), 'Cursors subscription callback');
          callback({});
        }
      }, 
      (error) => {
        logError(createError(error, ERROR_TYPES.NETWORK, 'high'), 'Cursors subscription listener');
        // Call callback with empty object on error
        callback({});
      }
    );

    return unsubscribe;
  } catch (error) {
    logError(createError(error, ERROR_TYPES.NETWORK, 'high'), 'Setting up cursors subscription');
    // Return no-op function if setup fails
    return () => {};
  }
}

/**
 * Set up automatic cursor cleanup on disconnect
 * @param {string} userId - User ID to set up cleanup for
 * @returns {Promise<Object>} Result with success status
 */
export async function setupCursorCleanup(userId) {
  return await withErrorHandling(
    async () => {
      // Validate input
      if (!userId) {
        throw createError(
          'User ID is required',
          ERROR_TYPES.VALIDATION,
          'medium'
        );
      }

      const userSessionPath = RTDB_PATHS.getUserSession(userId);
      const userRef = ref(rtdb, userSessionPath);
      
      // Auto-remove cursor when user disconnects
      await onDisconnect(userRef).remove();
      console.log('🧹 Cursor cleanup configured for user:', userId);
      
      return {
        success: true,
        message: 'Cursor cleanup configured'
      };
    },
    'Setup Cursor Cleanup',
    { 
      errorType: ERROR_TYPES.NETWORK,
      enableRetry: true,
      enableOfflineQueue: false // Don't queue cleanup operations
    }
  );
}

/**
 * Manually remove a user's cursor (for logout/unmount)
 * @param {string} userId - User ID to remove cursor for
 * @returns {Promise<void>}
 */
export async function removeCursor(userId) {
  try {
    const userSessionPath = RTDB_PATHS.getUserSession(userId);
    const userRef = ref(rtdb, userSessionPath);
    
    await remove(userRef);
    console.log('Cursor removed for user:', userId);
  } catch (error) {
    console.error('Error removing cursor:', error);
    throw error;
  }
}

/**
 * Initialize cursor presence for a user
 * @param {string} userId - User ID
 * @param {string} displayName - User's display name
 * @param {string} color - User's cursor color
 * @returns {Promise<void>}
 */
export async function initializeCursorPresence(userId, displayName, color) {
  try {
    // Set up automatic cleanup first
    await setupCursorCleanup(userId);
    
    // Create comprehensive user presence data
    const userSessionPath = RTDB_PATHS.getUserSession(userId);
    const userRef = ref(rtdb, userSessionPath);
    
    // Store complete user presence data (includes color assignment)
    await set(userRef, {
      userId,
      displayName,
      cursorColor: color,
      cursorX: 0,
      cursorY: 0,
      lastSeen: serverTimestamp(),
      isOnline: true,
      joinedAt: serverTimestamp()
    });
    
    console.log('Cursor presence initialized for:', displayName, 'with color:', color);
  } catch (error) {
    console.error('Error initializing cursor presence:', error);
    throw error;
  }
}

/**
 * Clean up cursor on component unmount or logout
 * @param {string} userId - User ID
 * @returns {Promise<void>}
 */
export async function cleanupCursorPresence(userId) {
  try {
    await removeCursor(userId);
    console.log('Cursor presence cleaned up for user:', userId);
  } catch (error) {
    console.error('Error cleaning up cursor presence:', error);
    // Don't throw - cleanup should be non-blocking
  }
}
